import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  Card,
  FontIcon,
  FontSizes,
  Heading,
  Link,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { SettingsLabels } from "@modules/settings/screens/shared-components/SettingsLabels.ts";
import { getUserStylesSet } from "@modules/settings/screens/users/components/UserScreens.styles.tsx";
import { useStores } from "@stores/hooks/useStores.ts";
import { SubmissionForm } from "@ui-components/form/submission-form/SubmissionForm.tsx";
import { ToggleField } from "@ui-components/form/Toggle/ToggleField.tsx";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";
import { When } from "@ui-components/withPerm.tsx";

import {
  AppointmentSettingsValues,
  nameOf
} from "./AppointmentSettings.types.ts";
import { EditBaseIntervalModal } from "./EditBaseIntervalModal.tsx";

const KNOWLEDGE_BASE_URL =
  "https://kb.bpsoftware.net/bpomni/Management/BaseIntervals.htm";

const SUPPORT_URL = "https://bpomni.co.nz/support/";

export const AppointmentSettingsCard: React.FC = observer(() => {
  const { core, notification, userExperience } = useStores();
  const { apptTypeBaseInterval } = userExperience.ui;
  const { autoLinkCondition, captureApptCancellationReason } =
    userExperience.orgUnitSettingForLocation?.preferenceSetting || {};

  const patientLabel = usePatientLabel();

  const [modalOpen, setModalOpen] = useState(false);

  const theme = useTheme();
  const { formFooter, formFields } = getUserStylesSet(theme);

  const onSubmitAppointmentSetting = async (
    values: AppointmentSettingsValues
  ) => {
    const { autoLinkCondition, captureApptCancellationReason } = values;

    await Promise.all(
      core.allLocations.map(location =>
        userExperience.updateOrgUnitSetting({
          orgUnitId: location.id,
          preferenceSetting: {
            autoLinkCondition,
            captureApptCancellationReason
          },
          eTag: userExperience.orgUnitSettingMap.get(location.id)?.eTag || ""
        })
      )
    );
  };

  return (
    <Card
      iconName="calendar"
      headingLevel="section-heading"
      heading={SettingsLabels.appointmentsSettings}
      styles={{ subComponentStyles: { tile: { content: { padding: 0 } } } }}
    >
      <Stack tokens={{ childrenGap: 8 }} styles={{ root: formFields }}>
        <Text bold>
          Length base interval (currently {apptTypeBaseInterval}min)
        </Text>

        <MessageBar messageBarType={MessageBarType.warning}>
          <Stack tokens={{ childrenGap: 16 }}>
            <Text styles={{ root: { fontSize: FontSizes.size12 } }} block>
              Appointment lengths are multiples of the base interval.
            </Text>
            <Text styles={{ root: { fontSize: FontSizes.size12 } }} block>
              When you set the base interval, the length of every appointment
              type in the practice may change. The calendar slots will change.
              Your existing appointments <b>will not be altered</b>.
            </Text>
            <Text styles={{ root: { fontSize: FontSizes.size12 } }} block>
              Any appointments booked (or edited) after altering the appointment
              base interval length must have a length which is a multiple of the
              new base.
            </Text>
            <Text styles={{ root: { fontSize: FontSizes.size12 } }} block>
              <b>We recommend reading the </b>
              <Link href={KNOWLEDGE_BASE_URL} target="_blank">
                knowledge base
              </Link>
              <b>, and </b>
              <Link href={SUPPORT_URL} target="_blank">
                contacting support
              </Link>
              <b> before changing the appointment base interval</b>
            </Text>{" "}
          </Stack>
          <Stack horizontalAlign="end" styles={{ root: { marginTop: 12 } }}>
            <Link
              onClick={() => setModalOpen(true)}
              styles={{ root: { fontSize: FontSizes.size14 } }}
            >
              Change the appointment length base interval
            </Link>
          </Stack>
        </MessageBar>
      </Stack>

      <SubmissionForm<AppointmentSettingsValues>
        formName="appointment-settings"
        onSubmitSucceeded={() => {
          notification.success("Appointment settings has been updated.");
        }}
        readOnly={!core.hasPermissions(Permission.TenantSettingWrite)}
        autoFocus={false}
        onSubmit={onSubmitAppointmentSetting}
        initialValues={{
          autoLinkCondition:
            autoLinkCondition !== undefined ? autoLinkCondition : true,
          captureApptCancellationReason:
            captureApptCancellationReason !== undefined
              ? captureApptCancellationReason
              : true
        }}
        buttonsProps={{
          disableCancelOnPristine: true,
          styles: { root: formFooter }
        }}
        styles={{ fields: formFields }}
      >
        {() => (
          <Stack tokens={{ childrenGap: 4 }}>
            <Heading labelPaddings>Cancellation reason</Heading>
            <ToggleField
              onText="Required when cancelling"
              offText="Optional when cancelling"
              name={nameOf("captureApptCancellationReason")}
            />
            <When permission={[Permission.ConditionsAllowed]}>
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 4 }}
              >
                <Heading labelPaddings>Automatically link conditions</Heading>
                <TooltipHost
                  content={`Linking will only automatically occur if the ${patientLabel} has one open condition`}
                  styles={{ root: { display: "flex" } }}
                >
                  <FontIcon
                    iconName="Info"
                    styles={{ root: { fontSize: FontSizes.size14 } }}
                  />
                </TooltipHost>
              </Stack>
              <ToggleField
                onText="Open condition will be linked to a new appointment"
                offText="Open condition will not be linked automatically"
                name={nameOf("autoLinkCondition")}
                styles={{ root: { marginTop: 8 } }}
              />
            </When>
          </Stack>
        )}
      </SubmissionForm>

      <EditBaseIntervalModal
        hidden={!modalOpen}
        currentValue={apptTypeBaseInterval}
        onDismiss={() => setModalOpen(false)}
      />
    </Card>
  );
});
